// FILE: /src/Components/Publishing/LinksDashboard.js (FULLY UPDATED)

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button, Spin, Empty, message, Table, Tag, Space, Popconfirm, Modal, Avatar, Tooltip, Input, Switch, Checkbox, List } from 'antd';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import moment from 'moment';
import axiosServices from '../../services/axios';
import { useSelector } from 'react-redux';
import { fetchDrafts } from '../../services/group';
import CreateLinkModal from './CreateLinkModal';
import { LockOutlined, DeleteOutlined, EyeOutlined, MessageOutlined, LikeOutlined } from '@ant-design/icons';

// --- Redesigned Edit & Review Modal Component ---
const EditLinkModal = ({ link, isOpen, onClose, onUpdate }) => {
    const [title, setTitle] = useState('');
    const [isPasswordProtected, setIsPasswordProtected] = useState(false);
    const [password, setPassword] = useState('');
    // --- THIS IS THE FIX: removePassword state is no longer needed ---
    // const [removePassword, setRemovePassword] = useState(false); 
    const [selectedPostIds, setSelectedPostIds] = useState([]);
    const [allAvailablePosts, setAllAvailablePosts] = useState([]);
    const [postsWithFeedback, setPostsWithFeedback] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const { selectedGroup, groups } = useSelector((state) => state.groups);

    const fetchData = useCallback(async () => {
        if (!isOpen || !link) return;
        setLoading(true);
        try {
            const groupId = groups.find(g => g.name === selectedGroup)?._id;
            const [postsResponse, detailsResponse] = await Promise.all([
                fetchDrafts(groupId),
                axiosServices.get(`/api/links/details/${link._id}`)
            ]);
            
            const draftAndScheduled = (postsResponse || []).filter(p => p.status === 'scheduled' || p.status === 'draft');
            setAllAvailablePosts(draftAndScheduled);
            setPostsWithFeedback(detailsResponse.data.posts || []);
            
            setTitle(link.title);
            setIsPasswordProtected(link.hasPassword);
            setSelectedPostIds(link.postIds);
            setPassword('');
        } catch (error) {
            message.error("Could not load link details for editing.");
        } finally {
            setLoading(false);
        }
    }, [isOpen, link, selectedGroup, groups]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDeleteComment = async (commentId) => {
        try {
            await axiosServices.delete(`/api/links/comments/${commentId}`);
            message.success("Comment deleted.");
            fetchData();
            onUpdate();
        } catch (error) {
            message.error("Failed to delete comment.");
        }
    };

    const handleSelectPost = (postId) => {
        setSelectedPostIds(prev => prev.includes(postId) ? prev.filter(id => id !== postId) : [...prev, postId]);
    };

    const handleSaveChanges = async () => {
        setSaving(true);
        try {
            const payload = { title, postIds: selectedPostIds };
            
            // --- THIS IS THE FIX: Simplified password logic ---
            if (isPasswordProtected) {
                // Only include the password if the user has typed a new one
                if (password) {
                    payload.password = password;
                }
            } else {
                // If the switch is off but the link originally had a password, signal removal
                if (link.hasPassword) {
                    payload.removePassword = true;
                }
            }
            // --- END OF FIX ---

            await axiosServices.put(`/api/links/${link._id}`, payload);
            message.success("Link updated successfully!");
            onUpdate();
            onClose();
        } catch (error) {
            message.error("Failed to update link.");
        } finally {
            setSaving(false);
        }
    };
    
    const postsToShowForSelection = useMemo(() => {
        const postMap = new Map();
        [...postsWithFeedback, ...allAvailablePosts].forEach(post => {
            if(post) postMap.set(post._id, post);
        });
        return Array.from(postMap.values()).map(post => {
            const feedback = postsWithFeedback.find(p => p._id === post._id);
            return {
                ...post,
                likes: feedback?.likes || 0,
                comments: feedback?.comments || []
            };
        });
    }, [allAvailablePosts, postsWithFeedback]);

    if (!link) return null;

    return (
        <Modal
            title={<span className="font-bold text-xl text-gray-800">Edit & Review Link</span>}
            open={isOpen}
            onCancel={onClose}
            width={1000}
            footer={[
                <Button key="cancel" onClick={onClose}>Cancel</Button>,
                <Button key="save" type="primary" loading={saving} onClick={handleSaveChanges}>Save Changes</Button>
            ]}
            styles={{ body: { backgroundColor: '#f9fafb', padding: 0 } }}
        >
            {loading ? <div className="flex justify-center items-center h-96"><Spin /></div> : (
                <div className="grid grid-cols-1 md:grid-cols-2">
                    {/* Left Column: Edit Form */}
                    <div className="p-6 border-r border-gray-200 space-y-6">
                        <div>
                            <label className="font-semibold text-gray-800 mb-2 block">Link Title</label>
                            <Input value={title} onChange={e => setTitle(e.target.value)} size="large" />
                        </div>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                               <label className="font-semibold text-gray-800">Password Protection</label>
                               <Switch checked={isPasswordProtected} onChange={setIsPasswordProtected} />
                            </div>
                            {/* --- THIS IS THE FIX: Simplified UI --- */}
                            {isPasswordProtected && (
                                <Input.Password 
                                    placeholder={link.hasPassword ? "Enter new password to change" : "Enter a password"} 
                                    value={password} 
                                    onChange={e => setPassword(e.target.value)} 
                                    prefix={<LockOutlined />} 
                                />
                            )}
                            {/* --- END OF FIX --- */}
                        </div>
                        <div>
                            <label className="font-semibold text-gray-800 mb-2 block">Select Posts ({selectedPostIds.length})</label>
                            <div className="border rounded-lg max-h-[400px] overflow-y-auto bg-white">
                                <List
                                    dataSource={postsToShowForSelection}
                                    renderItem={(post) => {
                                        if (!post) return null;
                                        const isSelected = selectedPostIds.includes(post._id);
                                        return (
                                            <List.Item key={post._id} onClick={() => handleSelectPost(post._id)} className={`cursor-pointer ${isSelected ? 'bg-blue-50 border-blue-200' : 'hover:bg-gray-50'}`} actions={[<Checkbox checked={isSelected} readOnly/>]}>
                                                <List.Item.Meta
                                                    avatar={<Avatar shape="square" src={post.media_url?.[0]} className="bg-gray-200"/>}
                                                    title={<span className="font-medium text-sm text-gray-800 truncate block max-w-xs">{post.message || `Post from ${moment(post.createdAt).format('MMM D')}`}</span>}
                                                    description={
                                                        <div className="flex items-center justify-between">
                                                            <Tag color={post.status === 'draft' ? 'gold' : 'blue'}>{post.status}</Tag>
                                                            <Space size="middle" className="text-xs text-gray-500">
                                                                {post.likes > 0 && <span><LikeOutlined /> {post.likes}</span>}
                                                                {post.comments.length > 0 && <span><MessageOutlined /> {post.comments.length}</span>}
                                                            </Space>
                                                        </div>
                                                    }
                                                />
                                            </List.Item>
                                        );
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    {/* Right Column: Feedback */}
                    <div className="p-6 space-y-4">
                        <label className="font-semibold text-gray-800 mb-1 block">Feedback Received</label>
                        <div className="max-h-[550px] overflow-y-auto space-y-4 pr-2">
                            {postsWithFeedback.filter(p => selectedPostIds.includes(p._id) && (p.likes > 0 || p.comments.length > 0)).length > 0 ? 
                                postsWithFeedback.map(post => (
                                (selectedPostIds.includes(post._id) && (post.likes > 0 || post.comments.length > 0)) &&
                                <div key={post._id} className="bg-white rounded-lg border border-gray-200 p-3">
                                    <div className="flex items-start gap-3">
                                        <Avatar shape="square" size={40} src={post.media_url?.[0]} className="bg-gray-200 flex-shrink-0" />
                                        <div className="flex-grow">
                                            <p className="text-xs font-semibold truncate text-gray-700">{post.message || "Untitled Post"}</p>
                                            <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                                                <span className="flex items-center gap-1.5"><LikeOutlined /> {post.likes}</span>
                                                <span className="flex items-center gap-1.5"><MessageOutlined /> {post.comments.length}</span>
                                            </div>
                                        </div>
                                    </div>
                                    {post.comments.length > 0 && (
                                        <div className="mt-2 pt-2 border-t border-gray-100 space-y-2">
                                            {post.comments.map(comment => (
                                                <div key={comment._id} className="text-xs bg-gray-50 p-2 rounded flex justify-between items-start">
                                                    <div className="flex-grow pr-2">
                                                        <p className="text-gray-800">{comment.content}</p>
                                                        <p className="text-gray-400 mt-1">{moment(comment.createdAt).format('MMM D, h:mm A')}</p>
                                                    </div>
                                                    <Popconfirm title="Delete this comment?" onConfirm={() => handleDeleteComment(comment._id)}>
                                                        <Button type="text" danger shape="circle" size="small" icon={<DeleteOutlined />} />
                                                    </Popconfirm>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No feedback on selected posts." />}
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
};

// ... (The rest of the LinksDashboard component is unchanged)
const LinksDashboard = (props) => {
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [postsForModal, setPostsForModal] = useState([]);
    const [postsLoading, setPostsLoading] = useState(false);
    const [editModalState, setEditModalState] = useState({ visible: false, link: null });
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const { selectedGroup, groups } = useSelector((state) => state.groups);

    const fetchLinks = async () => {
        try {
            setLoading(true);
            const response = await axiosServices.get('/api/links');
            setLinks(response.data);
        } catch (err) {
            setError('Failed to fetch your links. Please try again.');
            message.error(err.response?.data?.message || 'Could not fetch links.');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchLinks();
    }, []);
    const handleOpenCreateModal = async () => {
        if (!selectedGroup) {
            message.error("Please select a brand/group first.");
            return;
        }
        setPostsLoading(true);
        try {
            const groupId = groups.find((g) => g.name === selectedGroup)?._id;
            if (groupId) {
                const posts = await fetchDrafts(groupId);
                const draftAndScheduledPosts = (posts || []).filter(p => p.status === 'scheduled' || p.status === 'draft');
                setPostsForModal(draftAndScheduledPosts);
                setIsCreateModalOpen(true);
            } else {
                throw new Error("Could not find the selected group's ID.");
            }
        } catch (error) {
            message.error("Could not load posts to share. Please try again.");
        } finally {
            setPostsLoading(false);
        }
    };
    const handleDelete = async (linkId) => {
        try {
            await axiosServices.delete(`/api/links/${linkId}`);
            message.success('Link deleted successfully.');
            fetchLinks();
        } catch (err) {
            message.error('Failed to delete the link.');
        }
    };
    const handleBulkDelete = () => {
        Modal.confirm({
            title: `Delete ${selectedRowKeys.length} selected links?`,
            content: "This action is permanent and cannot be undone.",
            okText: "Delete All",
            okType: 'danger',
            onOk: async () => {
                const deletePromises = selectedRowKeys.map(id => axiosServices.delete(`/api/links/${id}`));
                try {
                    await Promise.all(deletePromises);
                    message.success(`${selectedRowKeys.length} links deleted.`);
                    setSelectedRowKeys([]);
                    fetchLinks();
                } catch (error) {
                    message.error("Some links could not be deleted.");
                }
            }
        });
    };
    const rowSelection = {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
    };
    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            render: (text, record) => <button onClick={() => setEditModalState({ visible: true, link: record })} className="font-semibold text-blue-600 hover:underline bg-transparent border-none cursor-pointer p-0 text-left flex items-center gap-2">{text}</button>,
        },
        {
            title: 'Stats',
            key: 'stats',
            render: (_, record) => (
                <Space size="middle">
                    <Tooltip title="Total Views"><span className="flex items-center gap-1"><i className="fa-regular fa-eye text-gray-500"></i> {record.viewCount || 0}</span></Tooltip>
                    <Tooltip title="Likes"><span className="flex items-center gap-1"><i className="fa-regular fa-thumbs-up text-gray-500"></i> {record.likeCount || 0}</span></Tooltip>
                    <Tooltip title="Comments"><span className="flex items-center gap-1"><i className="fa-regular fa-comment text-gray-500"></i> {record.commentCount || 0}</span></Tooltip>
                </Space>
            )
        },
        {
            title: 'Posts',
            dataIndex: 'postIds',
            key: 'posts',
            render: (postIds) => <Tag color="blue">{postIds.length}</Tag>,
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'created',
            render: (date) => moment(date).format('MMM D, YYYY'),
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'right',
            render: (_, record) => (
                <Space size="small">
                    {record.hasPassword && <Tooltip title="Password Protected"><i className="fa-solid fa-lock text-gray-500"></i></Tooltip>}
                    <Tooltip title="Preview Link in New Tab">
                        <Button size="small" icon={<EyeOutlined />} href={`/s/${record.shareableId}`} target="_blank" />
                    </Tooltip>
                    <CopyToClipboard text={`${window.location.origin}/s/${record.shareableId}`} onCopy={() => message.success('Link copied!')}>
                        <Button size="small">Copy Link</Button>
                    </CopyToClipboard>
                    <Popconfirm title="Delete this link?" onConfirm={() => handleDelete(record._id)} okText="Delete" okButtonProps={{ danger: true }}>
                        <Button size="small" danger>Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];
    if (error) {
        return <div className="text-center text-red-500 p-8">{error}</div>;
    }
    return (
        <>
            <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-full">
                <header className="flex flex-wrap items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Links</h1>
                        <p className="text-gray-500 text-sm mt-1">
                            Manage and track feedback on your shared content previews.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {selectedRowKeys.length > 0 && (
                            <Button danger onClick={handleBulkDelete}>Delete Selected ({selectedRowKeys.length})</Button>
                        )}
                        <Button type="primary" icon={<i className="fa-solid fa-plus mr-2"></i>} onClick={handleOpenCreateModal} loading={postsLoading}>
                            Create Link
                        </Button>
                    </div>
                </header>

                <Table
                    rowSelection={rowSelection}
                    dataSource={links}
                    columns={columns}
                    rowKey="_id"
                    loading={loading}
                    pagination={false}
                    className="bg-white rounded-lg shadow-sm border"
                    locale={{
                        emptyText: (
                            <Empty className="py-12" description={<span className="text-gray-500">You haven't created any shareable links yet.</span>}>
                                <Button type="primary" onClick={handleOpenCreateModal} loading={postsLoading}>
                                    Create Your First Link
                                </Button>
                            </Empty>
                        )
                    }}
                />
            </div>
            
            <CreateLinkModal
                isOpen={isCreateModalOpen}
                onClose={() => { setIsCreateModalOpen(false); fetchLinks(); }}
                posts={postsForModal}
            />

            <EditLinkModal
                isOpen={editModalState.visible}
                onClose={() => setEditModalState({ visible: false, link: null })}
                link={editModalState.link}
                onUpdate={fetchLinks}
            />
        </>
    );
};

export default LinksDashboard;